import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import './Auth.css';
import './CharacterSelection.css';
import './Profile.css';
import AuthLayeredCard from './AuthLayeredCard';
import AuthSelectionToolbar from './AuthSelectionToolbar';
import FriendTransferModal from './FriendTransferModal';
import { authButtonStyle, authButtonSmallStyle, authStatPanelStyle } from '../constants/authStyles';
import { formatNumber } from '../utils/localeFormat';
import { getActiveLocale } from '../utils/localeFormat';
import { translateGameName } from '../utils/gameNameI18n';
import { tl } from '../utils/inlineI18n';

const TABS = ['profile', 'friends', 'history'];
const pub = (p) => (p ? (p.startsWith('/') ? p : `/${p}`) : '');

/** Profil, arkadaşlar ve savaş geçmişi (sol üstteki oyuncu menüsünden açılır). */
const Profile = () => {
  const { t } = useTranslation('social');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const username = localStorage.getItem('username');
  const tab = TABS.includes(params.get('tab')) ? params.get('tab') : 'profile';
  const viewUser = (params.get('user') || '').trim();
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!username) navigate('/login');
  }, [username, navigate]);

  const flash = useCallback((text, bad = false) => setMessage(text ? { text, bad } : null), []);
  const setTab = (next, user) => {
    const q = { tab: next };
    if (user) q.user = user;
    setParams(q);
    setMessage(null);
  };

  return (
    <div className="auth-container auth-container--selection">
      <AuthLayeredCard className="profile-card">
        <AuthSelectionToolbar
          onBack={() => navigate('/game')}
          buttonStyle={authButtonStyle}
          header={(
            <>
              <h1>
                <img src="/image/menu/profil.png" alt="" className="profile-title-icon" draggable={false} />
                {viewUser && viewUser !== username ? viewUser : t(`tab_${tab}`)}
              </h1>
              <p>{t(`subtitle_${tab}`)}</p>
            </>
          )}
        />
        <div className="battle-nav-row profile-tabs">
          {TABS.map((k) => (
            <button
              key={k}
              type="button"
              className={`auth-button auth-button--compact battle-nav-btn${tab === k && !(k === 'profile' && viewUser && viewUser !== username) ? ' battle-subtab--active' : ''}`}
              style={authButtonSmallStyle}
              onClick={() => setTab(k)}
            >
              {t(`tab_${k}`)}
            </button>
          ))}
        </div>
        {message ? (
          <p className={`profile-msg${message.bad ? ' profile-msg--bad' : ''}`} role="status">{message.text}</p>
        ) : null}
        <div className="auth-selection-body profile-body">
          {tab === 'profile' && (
            <ProfileView key={viewUser || 'me'} user={viewUser} me={username} flash={flash} onOpenUser={(u) => setTab('profile', u)} />
          )}
          {tab === 'friends' && <FriendsView flash={flash} onOpenUser={(u) => setTab('profile', u)} />}
          {tab === 'history' && <HistoryView username={username} flash={flash} />}
        </div>
      </AuthLayeredCard>
    </div>
  );
};

/* ---------------- profil ---------------- */

function StatBox({ title, s, scoreLabel }) {
  const { t } = useTranslation('social');
  if (!s) return null;
  const total = (s.wins || 0) + (s.losses || 0) + (s.draws || 0);
  const rate = total > 0 ? Math.round(((s.wins || 0) / total) * 100) : 0;
  return (
    <div className="profile-stat">
      <div className="profile-stat-title">{title}</div>
      <div className="profile-stat-grid">
        <span>{t('wins')}</span><strong>{formatNumber(s.wins || 0)}</strong>
        <span>{t('losses')}</span><strong>{formatNumber(s.losses || 0)}</strong>
        {s.draws ? (<><span>{t('draws')}</span><strong>{formatNumber(s.draws)}</strong></>) : null}
        <span>{t('winRate')}</span><strong>%{rate}</strong>
        {scoreLabel ? (<><span>{scoreLabel}</span><strong>{formatNumber(s.score || 0)}</strong></>) : null}
        {s.position ? (<><span>{t('rank')}</span><strong>#{s.position}{s.rankTitle ? ` · ${s.rankTitle}` : ''}</strong></>) : null}
      </div>
    </div>
  );
}

function ProfileView({ user, me, flash, onOpenUser }) {
  const { t } = useTranslation('social');
  const [data, setData] = useState(null);
  const [about, setAbout] = useState('');
  const [editing, setEditing] = useState(false);
  const [pickAvatar, setPickAvatar] = useState(false);
  const [pw, setPw] = useState({ current: '', next: '', again: '' });
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const q = user && user !== me ? `?user=${encodeURIComponent(user)}` : '';
    try {
      const res = await axios.get(`/api/profile${q}`);
      if (!res.data?.success) {
        flash(res.data?.message || t('loadFailed'), true);
        return;
      }
      setData(res.data);
      setAbout(res.data.about || '');
    } catch (e) {
      flash(t('loadFailed'), true);
    }
  }, [user, me, flash, t]);

  useEffect(() => { load(); }, [load]);

  if (!data) return <p className="profile-empty">{t('loading')}</p>;
  const { user: card, stats, isMe } = data;

  const save = async (body, okText) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await axios.post('/api/profile/update', body);
      if (res.data?.success) {
        flash(okText);
        setEditing(false);
        setPickAvatar(false);
        load();
      } else {
        flash(res.data?.message || t('saveFailed'), true);
      }
    } catch (e) {
      flash(t('saveFailed'), true);
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.again) {
      flash(t('passwordMismatch'), true);
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post('/api/profile/password', { currentPassword: pw.current, newPassword: pw.next });
      if (res.data?.success) {
        flash(t('passwordChanged'));
        setPw({ current: '', next: '', again: '' });
      } else {
        flash(res.data?.message || t('saveFailed'), true);
      }
    } catch (err) {
      flash(t('saveFailed'), true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="profile-view">
      <div className="profile-head">
        <button
          type="button"
          className={`profile-avatar${isMe ? ' profile-avatar--edit' : ''}`}
          onClick={() => isMe && setPickAvatar((v) => !v)}
          disabled={!isMe}
          aria-label={isMe ? t('changeAvatar') : card.username}
          data-silent-click={isMe ? undefined : 'true'}
        >
          <img src={pub(card.avatar)} alt="" />
          <span className={`profile-dot${card.online ? ' is-online' : ''}`} />
        </button>
        <div className="profile-head-text">
          <div className="profile-name">{card.username}</div>
          {data.achievementSummary?.activeTitle ? (
            <div className="profile-title">🏷 {translateGameName(data.achievementSummary.activeTitle)}</div>
          ) : null}
          <div className="profile-sub">
            {card.className ? `${card.className} · ` : ''}{t('level')} {card.level || 0} · {card.online ? t('online') : t('offline')}
          </div>
          {isMe && data.email ? <div className="profile-sub">{t('email')}: {data.email}</div> : null}
        </div>
      </div>

      {isMe && pickAvatar ? (
        <div className="profile-avatars">
          {(data.avatars || []).map((a) => (
            <button key={a} type="button" className={`profile-avatar-opt${a === card.avatar ? ' is-active' : ''}`} onClick={() => save({ avatar: a }, t('avatarSaved'))}>
              <img src={pub(a)} alt="" />
            </button>
          ))}
        </div>
      ) : null}

      <div className="profile-section" style={authStatPanelStyle}>
        <div className="profile-section-title">{t('about')}</div>
        {isMe && editing ? (
          <>
            <textarea className="profile-textarea" maxLength={300} value={about} onChange={(e) => setAbout(e.target.value)} rows={3} />
            <div className="profile-row-end">
              <span className="profile-count">{about.length}/300</span>
              <button type="button" className="auth-button auth-button--compact" style={authButtonSmallStyle} onClick={() => setEditing(false)}>{t('cancel')}</button>
              <button type="button" className="auth-button auth-button--compact" style={authButtonSmallStyle} disabled={busy} onClick={() => save({ about }, t('aboutSaved'))}>{t('save')}</button>
            </div>
          </>
        ) : (
          <div className="profile-about">
            <p>{data.about || t('aboutEmpty')}</p>
            {isMe ? (
              <button type="button" className="auth-button auth-button--compact" style={authButtonSmallStyle} onClick={() => setEditing(true)}>{t('edit')}</button>
            ) : null}
          </div>
        )}
      </div>

      {!isMe ? <RelationActions user={card.username} relation={data.relation} flash={flash} onChange={load} /> : null}

      {data.achievementSummary ? (
        <div className="profile-section" style={authStatPanelStyle}>
          <div className="profile-section-title">
            {tl('Başarımlar', 'Achievements')} {data.achievementSummary.completed}/{data.achievementSummary.total}
          </div>
          {data.achievementSummary.titles?.length ? (
            <div className="profile-titles">
              {data.achievementSummary.titles.map((ti) => (
                <span key={ti} className={`profile-title-chip${ti === data.achievementSummary.activeTitle ? ' is-active' : ''}`}>
                  {translateGameName(ti)}
                </span>
              ))}
            </div>
          ) : (
            <div className="profile-sub">{tl('Henüz unvan yok. Görevler → Başarımlar sekmesinden takip edebilirsin.', 'No titles yet. Track them in Quests → Achievements.')}</div>
          )}
        </div>
      ) : null}

      <div className="profile-section" style={authStatPanelStyle}>
        <div className="profile-section-title">{t('statsTitle')}</div>
        <div className="profile-stats">
          <StatBox title={t('modeOffline')} s={stats?.offline} scoreLabel={t('points')} />
          <StatBox title={t('modeOnline')} s={stats?.online} scoreLabel={t('rating')} />
          <StatBox title={t('modeFriend')} s={stats?.friend} />
        </div>
        {stats?.seasons?.length ? (
          <div className="profile-seasons">
            <div className="profile-section-title">{t('seasonsTitle')}</div>
            {stats.seasons.map((s) => (
              <div key={`${s.year}-${s.month}-${s.mode}`} className="profile-season-row">
                <span>{String(s.month).padStart(2, '0')}/{s.year}</span>
                <span>{s.mode === 'online' ? t('modeOnline') : t('modeOffline')}</span>
                <span>#{s.position} · {s.rankTitle}</span>
                <span>{s.wins}/{s.losses}</span>
              </div>
            ))}
          </div>
        ) : (
          <p className="profile-muted">{t('seasonsEmpty')}</p>
        )}
      </div>

      {isMe ? (
        <form className="profile-section profile-password" style={authStatPanelStyle} onSubmit={changePassword} autoComplete="off">
          <div className="profile-section-title">{t('passwordTitle')}</div>
          {data.hasPassword ? (
            <input className="profile-input" type="password" autoComplete="current-password" placeholder={t('passwordCurrent')} value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} required />
          ) : (
            <p className="profile-muted">{t('passwordNone')}</p>
          )}
          <input className="profile-input" type="password" autoComplete="new-password" minLength={6} placeholder={t('passwordNew')} value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} required />
          <input className="profile-input" type="password" autoComplete="new-password" minLength={6} placeholder={t('passwordAgain')} value={pw.again} onChange={(e) => setPw({ ...pw, again: e.target.value })} required />
          <div className="profile-row-end">
            <button type="submit" className="auth-button auth-button--compact" style={authButtonSmallStyle} disabled={busy}>{t('passwordSubmit')}</button>
          </div>
        </form>
      ) : null}
      {!isMe ? (
        <button type="button" className="auth-button auth-button--compact profile-back-link" style={authButtonSmallStyle} onClick={() => onOpenUser('')}>{t('myProfile')}</button>
      ) : null}
    </div>
  );
}

/* ---------------- arkadaşlık düğmeleri ---------------- */

async function postFriend(path, body, flash, t) {
  try {
    const res = await axios.post(path, body);
    if (res.data?.success) {
      flash(res.data.message || t('done'));
      return res.data;
    }
    flash(res.data?.message || t('actionFailed'), true);
  } catch (e) {
    flash(e.response?.data?.message || t('actionFailed'), true);
  }
  return null;
}

function RelationActions({ user, relation, flash, onChange, compact = false, addLabel }) {
  const { t } = useTranslation('social');
  const [busy, setBusy] = useState(false);
  const [pickMode, setPickMode] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const act = async (path, body) => {
    if (busy) return;
    setBusy(true);
    await postFriend(path, body, flash, t);
    setBusy(false);
    setPickMode(false);
    onChange?.();
  };
  const btn = (label, path, body = { target: user }, extra = '') => (
    <button type="button" className={`auth-button auth-button--compact ${extra}`} style={authButtonSmallStyle} disabled={busy} onClick={() => act(path, body)}>
      {label}
    </button>
  );
  return (
    <div className={`profile-actions${compact ? ' profile-actions--compact' : ''}`}>
      {relation === 'none' && btn(addLabel || t('addFriend'), '/api/friends/request')}
      {relation === 'outgoing' && btn(t('cancelRequest'), '/api/friends/reject')}
      {relation === 'incoming' && (
        <>
          {btn(t('accept'), '/api/friends/accept')}
          {btn(t('reject'), '/api/friends/reject')}
        </>
      )}
      {relation === 'friends' && !pickMode && (
        <>
          <button type="button" className="auth-button auth-button--compact profile-duel" style={authButtonSmallStyle} disabled={busy} onClick={() => setPickMode(true)}>
            ⚔️ {t('friendlyBattle')}
          </button>
          <button type="button" className="auth-button auth-button--compact profile-duel" style={authButtonSmallStyle} disabled={busy} onClick={() => setTransferOpen(true)}>
            🎁 Transfer
          </button>
          {!compact && btn(t('removeFriend'), '/api/friends/remove', { target: user }, 'profile-danger')}
        </>
      )}
      {relation === 'friends' && pickMode && (
        <>
          {btn(t('battleOnline'), '/api/friends/battle', { target: user, mode: 'online' })}
          {btn(t('battleOffline'), '/api/friends/battle', { target: user, mode: 'offline' })}
          <button type="button" className="auth-button auth-button--compact" style={authButtonSmallStyle} onClick={() => setPickMode(false)}>
            {t('cancel')}
          </button>
        </>
      )}
      {transferOpen && (
        <FriendTransferModal target={user} flash={flash} onClose={() => setTransferOpen(false)} />
      )}
    </div>
  );
}

/* ---------------- arkadaşlar ---------------- */

function UserRow({ u, onOpenUser, children }) {
  const { t } = useTranslation('social');
  return (
    <div className="profile-user-row">
      <button type="button" className="profile-user" onClick={() => onOpenUser(u.username)}>
        <span className="profile-user-avatar">
          <img src={pub(u.avatar)} alt="" />
          <span className={`profile-dot${u.online ? ' is-online' : ''}`} />
        </span>
        <span className="profile-user-text">
          <strong>{u.username}</strong>
          <small>{t('level')} {u.level || 0} · {u.online ? t('online') : t('offline')}</small>
        </span>
      </button>
      {children}
    </div>
  );
}

function FriendsView({ flash, onOpenUser }) {
  const { t } = useTranslation('social');
  const [lists, setLists] = useState(null);
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [nonce, setNonce] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const seq = useRef(0);

  const load = useCallback(async () => {
    try {
      const res = await axios.get('/api/friends');
      if (res.data?.success) setLists(res.data);
    } catch (e) {
      flash(t('loadFailed'), true);
    }
  }, [flash, t]);

  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    const query = q.trim();
    if (!query) {
      setResults(null);
      return undefined;
    }
    const my = ++seq.current;
    const timer = setTimeout(async () => {
      try {
        const res = await axios.get(`/api/friends/search?q=${encodeURIComponent(query)}`);
        if (my === seq.current && res.data?.success) setResults(res.data.results || []);
      } catch (e) { /* yoksay */ }
    }, 300);
    return () => clearTimeout(timer);
  }, [q, nonce]);

  const refresh = () => {
    load();
    setNonce((n) => n + 1);
  };

  const closeAdd = () => {
    setAddOpen(false);
    setQ('');
  };

  return (
    <div className="profile-friends">
      <div className="profile-friends-top">
        <button type="button" className="auth-button profile-add-friend-btn" style={authButtonStyle} onClick={() => setAddOpen(true)}>
          ➕ {t('addFriendNew')}
        </button>
      </div>
      {addOpen ? (
        <div className="profile-popup-overlay" role="presentation" onClick={closeAdd}>
          <div className="profile-popup" role="dialog" aria-modal="true" aria-label={t('addFriendNew')} onClick={(e) => e.stopPropagation()}>
            <div className="profile-popup-head">
              <strong>{t('addFriendNew')}</strong>
              <button type="button" className="profile-popup-close" aria-label={t('close')} onClick={closeAdd}>×</button>
            </div>
            <input
              type="search"
              className="achievements-search-input profile-popup-search"
              placeholder={t('searchPlaceholder')}
              value={q}
              maxLength={40}
              autoFocus
              onChange={(e) => setQ(e.target.value)}
            />
            <div className="profile-popup-results">
              {results === null ? (
                <p className="profile-muted">{t('searchHint')}</p>
              ) : results.length === 0 ? (
                <p className="profile-muted">{t('searchNone')}</p>
              ) : results.map((u) => (
                <UserRow key={`s-${u.username}`} u={u} onOpenUser={(name) => { closeAdd(); onOpenUser(name); }}>
                  <RelationActions user={u.username} relation={u.relation} flash={flash} onChange={refresh} compact addLabel={t('addShort')} />
                </UserRow>
              ))}
            </div>
          </div>
        </div>
      ) : null}
      {!lists ? <p className="profile-empty">{t('loading')}</p> : (
        <>
          {lists.incoming?.length ? (
            <div className="profile-section" style={authStatPanelStyle}>
              <div className="profile-section-title">{t('incomingRequests')}</div>
              {lists.incoming.map((u) => (
                <UserRow key={`i-${u.username}`} u={u} onOpenUser={onOpenUser}>
                  <RelationActions user={u.username} relation="incoming" flash={flash} onChange={refresh} compact />
                </UserRow>
              ))}
            </div>
          ) : null}
          <div className="profile-section" style={authStatPanelStyle}>
            <div className="profile-section-title">{t('friendsTitle', { count: lists.friends?.length || 0 })}</div>
            {lists.friends?.length ? lists.friends.map((u) => (
              <UserRow key={`f-${u.username}`} u={u} onOpenUser={onOpenUser}>
                <RelationActions user={u.username} relation="friends" flash={flash} onChange={refresh} compact />
              </UserRow>
            )) : <p className="profile-muted">{t('friendsEmpty')}</p>}
          </div>
          {lists.outgoing?.length ? (
            <div className="profile-section" style={authStatPanelStyle}>
              <div className="profile-section-title">{t('outgoingRequests')}</div>
              {lists.outgoing.map((u) => (
                <UserRow key={`o-${u.username}`} u={u} onOpenUser={onOpenUser}>
                  <RelationActions user={u.username} relation="outgoing" flash={flash} onChange={refresh} compact />
                </UserRow>
              ))}
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

/* ---------------- savaş geçmişi ---------------- */

function HistoryView({ username, flash }) {
  const { t } = useTranslation('social');
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [page, setPage] = useState(0);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const u = encodeURIComponent(username);
        const [on, off] = await Promise.all([
          axios.get(`/api/arena/online/fights?username=${u}`),
          axios.get(`/api/arena/offline/fights?username=${u}`),
        ]);
        const tag = (list, src) => (list || []).map((f) => ({ ...f, src, delta: src === 'online' ? f.ratingDelta : f.pointsDelta }));
        // Yalnızca arkadaşlarla yapılan dostluk savaşları (arena maçları Arena ekranında).
        const all = [...tag(on.data?.fights, 'online'), ...tag(off.data?.fights, 'offline')]
          .filter((f) => f.kind === 'friend')
          .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
        if (alive) setRows(all);
      } catch (e) {
        if (alive) flash(t('loadFailed'), true);
      }
    })();
    return () => { alive = false; };
  }, [username, flash, t]);

  const watch = async (f) => {
    try {
      const path = f.src === 'online' ? '/api/arena/online/fight' : '/api/arena/offline/fight';
      const res = await axios.get(`${path}?username=${encodeURIComponent(username)}&id=${f.id}`);
      if (res.data?.success && res.data.fight) {
        navigate('/battle', { state: { arenaOfflineFight: res.data.fight } });
      } else {
        flash(res.data?.error || t('loadFailed'), true);
      }
    } catch (e) {
      flash(t('loadFailed'), true);
    }
  };

  if (!rows) return <p className="profile-empty">{t('loading')}</p>;
  if (!rows.length) return <p className="profile-empty">{t('historyEmpty')}</p>;
  const size = 8;
  const pages = Math.ceil(rows.length / size);
  const cur = Math.min(page, pages - 1);
  const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString(getActiveLocale(), { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return iso;
    }
  };
  const modeLabel = (f) => {
    const base = f.src === 'online' ? t('modeOnlineShort') : t('modeOfflineShort');
    return f.kind === 'friend' ? `${t('modeFriendShort')} · ${base}` : base;
  };
  return (
    <div className="profile-history profile-section" style={authStatPanelStyle}>
      <div className="profile-history-head">
        <span>{t('colOpponent')}</span><span>{t('colMode')}</span><span>{t('colResult')}</span><span>{t('colChange')}</span><span />
      </div>
      {rows.slice(cur * size, cur * size + size).map((f) => (
        <div key={`${f.src}-${f.id}`} className="profile-history-row">
          <span className="profile-history-opp">
            <strong>{f.opponent}</strong>
            <small>{fmtDate(f.createdAt)}</small>
          </span>
          <span>{modeLabel(f)}</span>
          <span className={f.draw ? '' : f.won ? 'profile-win' : 'profile-loss'}>{f.draw ? t('draw') : f.won ? t('win') : t('loss')}</span>
          <span className={f.delta > 0 ? 'profile-win' : f.delta < 0 ? 'profile-loss' : ''}>
            {f.kind === 'friend' ? '—' : `${f.delta > 0 ? '+' : ''}${f.delta || 0} ${f.src === 'online' ? t('rating') : t('points')}`}
          </span>
          <button type="button" className="auth-button auth-button--compact" style={authButtonSmallStyle} onClick={() => watch(f)}>{t('watch')}</button>
        </div>
      ))}
      {pages > 1 ? (
        <div className="profile-row-end">
          <button type="button" className="auth-button auth-button--compact" style={authButtonSmallStyle} disabled={cur <= 0} onClick={() => setPage(cur - 1)}>‹</button>
          <span className="profile-count">{cur + 1}/{pages}</span>
          <button type="button" className="auth-button auth-button--compact" style={authButtonSmallStyle} disabled={cur >= pages - 1} onClick={() => setPage(cur + 1)}>›</button>
        </div>
      ) : null}
    </div>
  );
}

export default Profile;
